<script setup lang="ts">
import { isFuture } from "date-fns";
import { onMounted, toRefs,watch,reactive, ref, computed } from "vue";
import { showBalloon,closeBalloon } from "../plugins/popup";
import { hankaku2Zenkaku,zenkaku2Hankaku,zenkakukana2Hankakukana,hira2kana } from "../plugins/utility";
import Breakpoint from "./Breakpoint.vue";

const props = defineProps({
    value: {
      type:[String,Number],
      required:true
    },
    type: {
      type:String,
      required:true
    },
    identityId: {
      type:String,
      default: () => {
        return '';
      }
    },
    width: {
      type:String,
      default: () => {
        return '100%';
      }
    },
    isFocusClear: {
      type:Boolean,
      default: () => {
        return false;
      }
    },
    isReadOnly: {
      type:Boolean,
      default: () => {
        return false;
      }
    },
    isTextFixed: {
      type:Boolean,
      default: () => {
        return false;
      }
    },
    isHoverPopup: {
      type:Boolean,
      default: () => {
        return false;
      }
    },
    isNoRoundedRight: {
      type:Boolean,
      default: () => {
        return false;
      }
    },
    isEnterKeyHint: {
      type:Boolean,
      default: () => {
        return false;
      }
    },
    onChange: {
      type:Function,
    },
    onEnter: {
      type:Function,
    },
    onClear: {
      type:Function,
    },
    onFocus: {
      type:Function,
    },
    onBlur: {
      type:Function,
    },
    fontSize: {
      type:String,
      default: () => {
        return '';
      }
    },
    fontLineHeight: {
      type:String,
      default: () => {
        return '';
      }
    },
    placeHolder: {
      type:String,
      default: () => {
        return '';
      }
    },
    selectedItemHolder: {
      type:String,
      default: () => {
        return '';
      }
    },
    fontWidth: {
      type:String,
      default: () => {
        return '';
      }
    },
    valueOptions:{
      type:Object,
      default: () => {
        return null;
      }
    },
    minimunNumber:{
      type:Number,
      default: () => {
        return null;
      }
    },
    textAreaRows:{
      type:Number,
      default: () => {
        return 4;
      }
    },
    minYear:{
      type:Number,
      default: () => {
        return new Date().getFullYear() - 5;
      }
    },
    maxYear:{
      type:Number,
      default: () => {
        return new Date().getFullYear() + 5;
      }
    }
})

const element = ref();
const { value } = toRefs(props);
// モバイルのみ表示する為のstatus
const isSp: boolean = Breakpoint().isBelow('sm');
const textValue = ref(props.value);
const preTextValue = ref(textValue.value);
const isFocus = ref(false);
const onChange = (value) => {
  if (typeof props.onChange !== 'undefined') {
    if (props.valueOptions !== null) {
      props.onChange(value,props.valueOptions);
    } else {
      props.onChange(value);
    }
  }
}
const isComposing = ref(false);
const onCompositionStart = () => {
  isComposing.value = true;
}
const onCompositionEnd = (e) => {
  isComposing.value = false;
  onTextChange(e);
}
const onTextChange = (e) => {
  // IME変換中(未確定)はここで書き換えるとIMEの表示・カーソル位置が壊れるので、確定するまで何もしない
  // (e.isComposingはChromeでcompositionend直後のinputイベントでもtrueのままになることがあるため使わない)
  if (isComposing.value) {
    return;
  }
  let value = e.target.value;
  if (props.fontWidth == 'half') {
    value = zenkaku2Hankaku(value);
  } else if (props.fontWidth == 'half_kana') {
    value = zenkakukana2Hankakukana(hira2kana(value));
  } else if (props.fontWidth == 'full') {
    value = hankaku2Zenkaku(value);
  }
  textValue.value = value;
  onChange(textValue.value);
}
// 年月選択(type:year_month)は年と月を選択してもらい、onChangeでは指定月の末日まで含めた"YYYY-MM-DD"形式で返す
const parseYearMonth = (value) => {
  let result = {year:'',month:''};
  if (typeof value === 'string' && value.length > 0) {
    let parts = value.split('-');
    if (parts.length >= 2) {
      result.year = parts[0];
      result.month = parts[1];
    }
  }
  return result;
}
const getLastDayOfMonth = (year,month) => {
  //JSのDateは月が0始まりなので、month(1始まり)の0日目=前月末日で末日を求める
  return String(new Date(Number(year),Number(month),0).getDate()).padStart(2,'0');
}
const yearMonthValue = reactive(parseYearMonth(props.value));
const yearOptions = computed(() => {
  let result = [];
  for (let y=props.minYear;y<=props.maxYear;y++) {
    result.push(String(y));
  }
  return result;
})
const monthOptions = computed(() => {
  let result = [];
  for (let m=1;m<=12;m++) {
    result.push(String(m).padStart(2,'0'));
  }
  return result;
})
const emitYearMonthChange = () => {
  if (yearMonthValue.year.length == 0 || yearMonthValue.month.length == 0) {
    return;
  }
  let lastDay = getLastDayOfMonth(yearMonthValue.year,yearMonthValue.month);
  textValue.value = yearMonthValue.year + '-' + yearMonthValue.month + '-' + lastDay;
  onChange(textValue.value);
}
const onYearChange = (e) => {
  yearMonthValue.year = e.target.value;
  emitYearMonthChange();
}
const onMonthChange = (e) => {
  yearMonthValue.month = e.target.value;
  emitYearMonthChange();
}
const clearYearMonth = () => {
  yearMonthValue.year = '';
  yearMonthValue.month = '';
  textValue.value = '';
  onChange(textValue.value);
}
//watch(textValue,(newValue,oldValue) => {
//  onChange(newValue);
//})
watch(value,(newValue,oldValue) => {
  textValue.value = newValue;
  if (props.type == 'year_month') {
    let parsed = parseYearMonth(newValue);
    yearMonthValue.year = parsed.year;
    yearMonthValue.month = parsed.month;
  }
})
const onFocus = (e) => {
  isFocus.value = true;
  preTextValue.value = textValue.value;
  if (props.isFocusClear && !props.isReadOnly && props.type != 'year_month') {
    element.value.select();
  }
  if (typeof props.onFocus !== 'undefined') {
    if (props.valueOptions !== null) {
      props.onFocus(textValue.value,props.valueOptions);
    } else {
      props.onFocus(textValue.value);
    }
  }
  if (isSp && props.type != 'date' && props.type != 'datetime-local') {
    //現在位置までスクロールする(アプリの場合タイムラグがあるのでインターバルを設ける)
    //dateタイプはスクロールするとネイティブの日付ピッカーが閉じてしまう為、対象外にする
    setTimeout(() => {
      element.value.scrollIntoView();
    },500);
  }
}
const onBlur = (e) => {
  setTimeout(function(){
      isFocus.value = false;
      if (typeof props.onBlur !== 'undefined') {
        if (props.valueOptions !== null) {
          props.onBlur(textValue.value,props.valueOptions);
        } else {
          props.onBlur(textValue.value);
        }
      }
    },100);
}
const keyEnter = (e) => {
  if (typeof props.onEnter !== 'undefined') {
    if (props.valueOptions !== null) {
      props.onEnter(textValue.value,props.valueOptions);
    } else {
      props.onEnter(textValue.value);
    }
  }
}

const textChangeCancel = (e) => {
  changeCancel();
}
const hoverText = (event) => {
  if (props.isHoverPopup && textValue.value.trim().length > 0 && !isFocus.value) {
    showBalloon(event,textValue.value.trim())
  }
}
const leaveText = (event) => {
  closeBalloon(event)
}
watch(isFocus,(newValue,oldValue) => {
  closeBalloon(null)
});
const changeCancel = () => {
  textValue.value = preTextValue.value;
  setTimeout(function(){
      if (typeof props.onClear !== 'undefined') {
        if (props.valueOptions !== null) {
          props.onClear(textValue.value,props.valueOptions);
        } else {
          props.onClear(textValue.value);
        }
      }
      if (typeof props.onChange !== 'undefined') {
        if (props.valueOptions !== null) {
          props.onChange(textValue.value,props.valueOptions);
        } else {
          props.onChange(textValue.value);
        }
      }
    },100);
}
const isEdited = () => {
  return preTextValue.value != textValue.value && isFocus.value;
}
const setFocus = () => {
  element.value.focus()
}
const leaveFocus = () => {
  element.value.blur()
}
defineExpose({
  changeCancel,
  isEdited,
  setFocus,
  leaveFocus,
});

</script>

<style scoped>
.text-box {
  color:black;
}
::placeholder {
  color: rgb(203, 202, 202);
}
.read-only {
  background-color: #e9e7e7;
  color:#918f8f;
}
.selected-itemholder::placeholder {
  color:rgb(81, 81, 81);
}
textarea {
  resize: none;
}
</style>

<template>
  <div class="w-full flex text-box">
    <div class="w-full relative">
      <textarea ref="element" v-if="type == 'textarea'" :rows="textAreaRows" @focus="onFocus" @blur="onBlur" @input="onTextChange" @compositionstart="onCompositionStart" @compositionend="onCompositionEnd" @keydown.enter="keyEnter" :placeholder="(selectedItemHolder ? selectedItemHolder : placeHolder)" :class="(isReadOnly ? 'read-only ' : '') + (isNoRoundedRight ? 'rounded-l' : 'rounded') + ' w-full border-gray-300 focus:border-amber-500 focus:ring-amber-500 text-xs md:text-base ' + (selectedItemHolder ? 'selected-itemholder' : '')" :readonly="isReadOnly || isTextFixed" :style="'width:' + width + ';' + (fontSize.length > 0 ? 'font-size:' + fontSize + ';' : '')">{{ textValue }}</textarea>
      <div v-else-if="type == 'year_month'" class="w-full flex items-center" :style="'width:' + width">
        <select ref="element" :value="yearMonthValue.year" @change="onYearChange" @focus="onFocus" @blur="onBlur" :disabled="isReadOnly || isTextFixed" :class="(isReadOnly ? 'read-only ' : '') + 'rounded border-gray-300 focus:border-amber-500 focus:ring-amber-500 text-xs md:text-base'">
          <option value="" disabled>年</option>
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
        <span class="mx-1">年</span>
        <select :value="yearMonthValue.month" @change="onMonthChange" @focus="onFocus" @blur="onBlur" :disabled="isReadOnly || isTextFixed" :class="(isReadOnly ? 'read-only ' : '') + 'rounded border-gray-300 focus:border-amber-500 focus:ring-amber-500 text-xs md:text-base'">
          <option value="" disabled>月</option>
          <option v-for="m in monthOptions" :key="m" :value="m">{{ Number(m) }}</option>
        </select>
        <span class="mx-1">月</span>
        <span v-show="!isReadOnly && !isTextFixed && (yearMonthValue.year.length > 0 || yearMonthValue.month.length > 0)" @click="clearYearMonth" class="cursor-pointer ml-1 text-lg"><i class="fa-solid fa-xmark"></i></span>
      </div>
      <input ref="element" v-else :data-identity-id="identityId" :type="type" :min="minimunNumber != null ? String(minimunNumber) : ''" :enterkeyhint="isEnterKeyHint ? 'enter' : ''" @focus="onFocus" @blur="onBlur" @mouseover="hoverText" @mouseout="leaveText" @input="onTextChange" @compositionstart="onCompositionStart" @compositionend="onCompositionEnd" :value="textValue" @keydown.enter="keyEnter" :placeholder="(selectedItemHolder ? selectedItemHolder : placeHolder)" :class="(isReadOnly ? 'read-only ' : '') + (isNoRoundedRight ? 'rounded-l' : 'rounded') + ' w-full border-gray-300 focus:border-amber-500 focus:ring-amber-500 text-xs md:text-base ' + (selectedItemHolder ? 'selected-itemholder' : '')" :readonly="isReadOnly || isTextFixed" :style="'width:' + width + ';' + (fontSize.length > 0 ? 'font-size:' + fontSize + ';' : '') + (fontLineHeight.length > 0 ? 'line-height:' + fontLineHeight + ';' : '')">
      <span v-show="preTextValue != textValue && isFocus && type != 'textarea' && type != 'year_month'" @click="textChangeCancel" :class="'cursor-pointer absolute top-1 text-lg ' + (type == 'number' ? 'right-8' : 'right-2')"><i class="fa-solid fa-xmark"></i></span>
    </div>
  </div>
</template>

